import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { FieldError } from '@/features/auth/components/FieldError';
import { LOGIN_OTP_LENGTH } from '@/features/auth/utils/loginIdentifier';

type LoginOtpFieldsProps = {
  otpDigits: string[];
  otpError: string;
  onOtpChange: (next: string[]) => void;
};

export function LoginOtpFields({
  otpDigits,
  otpError,
  onOtpChange,
}: LoginOtpFieldsProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const focusAt = (index: number) => {
    window.requestAnimationFrame(() => {
      const el = inputsRef.current[index];
      if (!el) return;
      el.focus();
      el.select();
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const next = [...otpDigits];
      next[index] = e.key;
      onOtpChange(next);
      if (index < LOGIN_OTP_LENGTH - 1) focusAt(index + 1);
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...otpDigits];
      if (next[index]) {
        next[index] = '';
        onOtpChange(next);
      } else if (index > 0) {
        next[index - 1] = '';
        onOtpChange(next);
        focusAt(index - 1);
      }
      return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === 'ArrowRight' && index < LOGIN_OTP_LENGTH - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handleChange = (raw: string, index: number) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    const next = [...otpDigits];
    next[index] = digit;
    onOtpChange(next);
    if (index < LOGIN_OTP_LENGTH - 1) focusAt(index + 1);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .trim()
      .split('')
      .filter((char) => /^[0-9]$/.test(char))
      .slice(0, LOGIN_OTP_LENGTH);

    if (pasted.length === 0) return;

    const next = [...otpDigits];
    pasted.forEach((char, i) => {
      next[i] = char;
    });
    onOtpChange(next);
    focusAt(Math.min(pasted.length, LOGIN_OTP_LENGTH - 1));
  };

  return (
    <div className="w-full min-w-0">
      <label className="mb-1.5 block text-[12.5px] font-semibold leading-none text-[#3E524F]">
        6-digit code
      </label>
      <div className="flex justify-start gap-1.5 sm:gap-2">
        {otpDigits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            className={`signup-input box-border h-11 w-10 shrink-0 rounded-[9px] border bg-white text-center text-base font-semibold text-[#0F1817] outline-none transition-[border-color,box-shadow] sm:h-12 sm:w-11 ${
              otpError
                ? 'border-red-400'
                : 'border-[#E2E9E7] hover:border-[#C9D3D1]'
            }`}
          />
        ))}
      </div>
      <FieldError message={otpError || undefined} />
    </div>
  );
}
